import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Zap, Shield, BarChart2, Key, Loader2, Code2, AppWindow, GitPullRequest, Network, FileCheck, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { captureUpgradePlanClicked, captureUpgradeConfirmed } from '../lib/analytics';

interface Props {
  open:    boolean;
  onClose: () => void;
}

const FREE_FEATURES = [
  { fr: '5 scans / jour',             en: '5 scans / day' },
  { fr: 'Rapport PDF basique',       en: 'Basic PDF report' },
  { fr: 'Historique des scans',      en: 'Scan history' },
  { fr: 'Score de sécurité /100',    en: 'Security score /100' },
  { fr: 'Découverte de surface d\'attaque (EASM)', en: 'Attack surface discovery (EASM)' },
];

const STARTER_FEATURES = [
  { icon: Zap,            fr: 'Scans illimités',                                          en: 'Unlimited scans' },
  { icon: BarChart2,      fr: 'Rapport PDF avancé + recommandations',                     en: 'Advanced PDF report + recommendations' },
  { icon: Shield,         fr: 'Monitoring continu + alertes email',                       en: 'Continuous monitoring + email alerts' },
  { icon: Network,        fr: 'Surveillance de surface d\'attaque + scan actif (EASM)',   en: 'Attack surface monitoring + active scan (EASM)' },
  { icon: Code2,          fr: 'Scan de code (dépôts GitHub) : secrets, dépendances, IaC', en: 'Code scan (GitHub repos): secrets, dependencies, IaC' },
  { icon: GitPullRequest, fr: 'Correction automatique par Pull Request (AutoFix)',        en: 'Automatic fix via Pull Request (AutoFix)' },
];

const PRO_FEATURES: { icon: typeof Zap; fr: string; en: string; soon?: boolean }[] = [
  { icon: Zap,       fr: 'Tout le plan Starter',                  en: 'Everything in Starter' },
  { icon: Shield,    fr: 'Rapports en marque blanche',            en: 'White-label reports' },
  { icon: Shield,    fr: 'Checks CVE / versions avancés',         en: 'Advanced CVE / version checks' },
  { icon: Code2,     fr: 'Webhooks sortants (Zapier, Slack…)',    en: 'Outbound webhooks (Zapier, Slack…)' },
  { icon: FileCheck, fr: 'Conformité NIS2 / RGPD',                en: 'NIS2 / GDPR compliance' },
  { icon: Network,   fr: 'Scan de vulnérabilités réseau / infrastructure', en: 'Network / infrastructure vulnerability scan', soon: true },
];

const DEV_FEATURES = [
  { icon: Zap,       fr: 'Tout le plan Pro',                                    en: 'Everything in Pro' },
  { icon: Key,       fr: 'Accès API (clé wsk_)',                                en: 'API access (wsk_ key)' },
  { icon: AppWindow, fr: 'Application Scanning (audit de vos apps web)',        en: 'Application Scanning (audit your web apps)' },
];

const ENTERPRISE_FEATURES = [
  { fr: 'SSO / SAML',                                en: 'SSO / SAML' },
  { fr: 'SLA & support dédié',                       en: 'SLA & dedicated support' },
  { fr: 'Scan réseau authentifié',                   en: 'Authenticated network scan' },
  { fr: 'Volumes & périmètres sur mesure',           en: 'Custom volumes & scope' },
];

export default function PricingModal({ open, onClose }: Props) {
  const { user, refreshUser, getPortalUrl, upgradeToPlan } = useAuth();
  const { lang } = useLanguage();

  const [success,       setSuccess]       = useState(false);
  const [portalError,   setPortalError]   = useState('');
  const [loadingPlan,   setLoadingPlan]   = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState('');

  // Fermeture par Escape
  const handleEscape = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  // Détecter le retour depuis Stripe (?payment=success)
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setSuccess(true);
      captureUpgradeConfirmed(params.get('plan') || undefined);   // conversion payante (événement « argent »)
      window.history.replaceState({}, '', window.location.pathname);
      refreshUser();
    }
  }, [open]);

  const plan      = user?.plan ?? 'free';
  const isPaid    = plan !== 'free';
  const isStarter = plan === 'starter';
  const isPro     = plan === 'pro';
  const isDev     = plan === 'dev';

  const handlePortal = async () => {
    setPortalError('');
    try {
      const url = await getPortalUrl();
      window.location.href = url;
    } catch {
      setPortalError(
        lang === 'fr'
          ? 'Impossible d\'ouvrir le portail. Réessayez dans quelques secondes.'
          : 'Unable to open portal. Please try again.'
      );
    }
  };

  // ── Boutons ───────────────────────────────────────────────────────────────────
  const handleUpgrade = async (plan: 'starter' | 'pro' | 'dev') => {
    if (!user) return;
    setCheckoutError('');
    setLoadingPlan(plan);
    captureUpgradePlanClicked(plan);
    try {
      const url = await upgradeToPlan(plan);
      window.location.href = url;
    } catch {
      setCheckoutError(
        lang === 'fr'
          ? 'Erreur lors de la création du paiement. Réessayez dans quelques secondes.'
          : 'Error creating checkout. Please try again.'
      );
      setLoadingPlan(null);
    }
  };

  // Couleurs par plan (fond / hover du bouton)
  const PLAN_BTN: Record<'starter' | 'pro' | 'dev', string> = {
    starter: 'bg-emerald-500 hover:bg-emerald-600 text-white',
    pro:     'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white',
    dev:     'bg-violet-500 hover:bg-violet-600 text-white',
  };

  const UpgradeBtn = ({ plan }: { plan: 'starter' | 'pro' | 'dev' }) => (
    <button
      onClick={() => handleUpgrade(plan)}
      disabled={!!loadingPlan}
      className={`w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-full py-2.5 text-sm font-bold transition-colors ${PLAN_BTN[plan]}`}
    >
      {loadingPlan === plan
        ? <Loader2 size={15} className="animate-spin" />
        : <Zap size={15} />}
      {loadingPlan === plan
        ? (lang === 'fr' ? 'Redirection...' : 'Redirecting...')
        : (lang === 'fr' ? 'Choisir ce plan' : 'Choose this plan')}
    </button>
  );

  const ActiveBadge = () => (
    <div className="w-full flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full py-2.5 text-sm font-medium text-emerald-600">
      <Check size={15} />
      {lang === 'fr' ? 'Plan actuel' : 'Current plan'}
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[rgba(15,18,34,0.45)] backdrop-blur-sm" onClick={onClose} />

          {/* Panel */}
          <motion.div
            className="relative z-10 w-full max-w-5xl sku-panel rounded-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div>
                <h2 className="text-lg font-bold text-[var(--color-ink)]">
                  {lang === 'fr' ? 'Choisissez votre plan' : 'Choose your plan'}
                </h2>
                <p className="text-xs text-[var(--color-muted)] mt-0.5">
                  {lang === 'fr'
                    ? 'Protégez tout votre périmètre — infrastructure et code. Sans engagement, résiliable à tout moment.'
                    : 'Protect your whole perimeter — infrastructure and code. No commitment, cancel anytime.'}
                </p>
              </div>
              <button onClick={onClose} className="text-[var(--color-faint)] hover:text-[var(--color-ink)] transition">
                <X size={20} />
              </button>
            </div>

            {/* Success banner */}
            {success && (
              <div className="mx-6 mt-4 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <Check size={16} className="text-emerald-500 shrink-0" />
                <p className="text-sm text-emerald-700">
                  {lang === 'fr'
                    ? 'Paiement reçu ! Votre plan est actif. Si le changement n\'apparaît pas, reconnectez-vous.'
                    : 'Payment received! Your plan is now active. If the change is not visible, please sign out and back in.'}
                </p>
              </div>
            )}

            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6">

              {/* ── FREE ── */}
              <div className="sku-card rounded-2xl p-5 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-[var(--color-muted)]">Free</span>
                  {plan === 'free' && (
                    <span className="text-xs bg-[var(--color-inset)] text-[var(--color-muted)] px-2 py-0.5 rounded-full">
                      {lang === 'fr' ? 'Plan actuel' : 'Current plan'}
                    </span>
                  )}
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-black text-[var(--color-ink)]">0€</span>
                  <span className="text-[var(--color-faint)] text-sm ml-1">/{lang === 'fr' ? 'mois' : 'month'}</span>
                </div>
                <ul className="space-y-2.5 flex-1">
                  {FREE_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-[var(--color-muted)]">
                      <Check size={14} className="text-[var(--color-faint)] shrink-0" />
                      {lang === 'fr' ? f.fr : f.en}
                    </li>
                  ))}
                </ul>
              </div>

              {/* ── STARTER ── */}
              <div className={`relative rounded-2xl p-5 overflow-hidden flex flex-col sku-card ${isStarter ? '!border-emerald-300 bg-emerald-50/50' : ''}`}>
                <div className="flex items-center justify-between mb-4 relative">
                  <span className="text-sm font-bold text-emerald-600">Starter</span>
                </div>

                <div className="mb-1 relative">
                  <span className="text-3xl font-black text-[var(--color-ink)]">34€</span>
                  <span className="text-[var(--color-muted)] text-sm ml-1">/{lang === 'fr' ? 'mois' : 'month'}</span>
                </div>
                <p className="text-xs text-[var(--color-muted)] mb-4">
                  {lang === 'fr' ? 'Idéal pour les PME' : 'Ideal for SMBs'}
                </p>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {STARTER_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-[var(--color-body)]">
                      <f.icon size={14} className="text-emerald-500 shrink-0" />
                      {lang === 'fr' ? f.fr : f.en}
                    </li>
                  ))}
                </ul>

                {isStarter
                  ? <ActiveBadge />
                  : user
                    ? <UpgradeBtn plan="starter" />
                    : (
                      <button
                        onClick={onClose}
                        className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 rounded-full py-2.5 text-sm font-bold text-white transition-colors"
                      >
                        <Zap size={15} />
                        {lang === 'fr' ? 'Créer un compte' : 'Create an account'}
                      </button>
                    )
                }
              </div>

              {/* ── PRO ── */}
              <div className="relative sku-card rounded-2xl p-5 overflow-hidden flex flex-col !border-2 !border-[var(--color-accent)]" style={{ boxShadow: 'var(--shadow-panel)' }}>
                <div className="flex items-center justify-between mb-4 relative">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[var(--color-accent-strong)]">Pro</span>
                    <span className="text-xs bg-[var(--color-accent-soft)] text-[var(--color-accent-strong)] border border-[rgba(13,148,136,0.25)] px-2 py-0.5 rounded-full font-semibold">
                      {lang === 'fr' ? 'Recommandé' : 'Recommended'}
                    </span>
                  </div>
                </div>

                <div className="mb-1 relative">
                  <span className="text-3xl font-black text-[var(--color-ink)]">99€</span>
                  <span className="text-[var(--color-muted)] text-sm ml-1">/{lang === 'fr' ? 'mois' : 'month'}</span>
                </div>
                <p className="text-xs text-[var(--color-muted)] mb-4">
                  {lang === 'fr' ? 'Pour les agences & PME' : 'For agencies & SMBs'}
                </p>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {PRO_FEATURES.map((f, i) => (
                    <li key={i} className={`flex items-center gap-2.5 text-sm ${f.soon ? 'text-[var(--color-muted)]' : 'text-[var(--color-body)]'}`}>
                      <f.icon size={14} className="text-[var(--color-accent-strong)] shrink-0" />
                      <span>{lang === 'fr' ? f.fr : f.en}</span>
                      {f.soon && (
                        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0">
                          {lang === 'fr' ? 'Bientôt' : 'Soon'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                {isPro
                  ? <ActiveBadge />
                  : user
                    ? <UpgradeBtn plan="pro" />
                    : (
                      <button
                        onClick={onClose}
                        className="sku-btn-primary w-full py-2.5 text-sm"
                      >
                        <Zap size={15} />
                        {lang === 'fr' ? 'Créer un compte' : 'Create an account'}
                      </button>
                    )
                }
              </div>

              {/* ── DEV ── */}
              <div className="relative sku-card rounded-2xl p-5 overflow-hidden flex flex-col !border-2 !border-violet-300" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="flex items-center justify-between mb-4 relative">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-violet-600">Business</span>
                    <span className="text-xs bg-violet-50 text-violet-600 border border-violet-200 px-2 py-0.5 rounded-full font-semibold">
                      {lang === 'fr' ? 'Équipes & intégrateurs' : 'Teams & integrators'}
                    </span>
                  </div>
                </div>

                <div className="mb-1 relative">
                  <span className="text-3xl font-black text-[var(--color-ink)]">149€</span>
                  <span className="text-[var(--color-muted)] text-sm ml-1">/{lang === 'fr' ? 'mois' : 'month'}</span>
                </div>
                <p className="text-xs text-[var(--color-muted)] mb-4">
                  {lang === 'fr' ? 'API + scan de vos propres apps' : 'API + scan your own apps'}
                </p>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {DEV_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-[var(--color-body)]">
                      <f.icon size={14} className="text-violet-500 shrink-0" />
                      {lang === 'fr' ? f.fr : f.en}
                    </li>
                  ))}
                </ul>

                {isDev
                  ? <ActiveBadge />
                  : user
                    ? <UpgradeBtn plan="dev" />
                    : (
                      <button
                        onClick={onClose}
                        className="w-full flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 rounded-full py-2.5 text-sm font-bold text-white transition-colors"
                      >
                        <Zap size={15} />
                        {lang === 'fr' ? 'Créer un compte' : 'Create an account'}
                      </button>
                    )
                }
              </div>
            </div>

            {/* ── ENTERPRISE (sur devis, hors Stripe) ── */}
            <div className="mx-6 mb-2 sku-card rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-1/4">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 size={16} className="text-[var(--color-ink)]" />
                  <span className="text-sm font-bold text-[var(--color-ink)]">Enterprise</span>
                </div>
                <span className="text-2xl font-black text-[var(--color-ink)]">
                  {lang === 'fr' ? 'Sur devis' : 'Custom'}
                </span>
                <p className="text-xs text-[var(--color-muted)] mt-1">
                  {lang === 'fr' ? 'Grands comptes & besoins sur mesure' : 'Large orgs & custom needs'}
                </p>
              </div>
              <ul className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {ENTERPRISE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-[var(--color-body)]">
                    <Check size={14} className="text-[var(--color-ink)] shrink-0" />
                    {lang === 'fr' ? f.fr : f.en}
                  </li>
                ))}
              </ul>
              <a
                href="mailto:contact@wezea.net?subject=Demande%20Enterprise%20Wezea"
                className="shrink-0 flex items-center justify-center gap-2 rounded-full py-2.5 px-5 text-sm font-bold bg-[var(--color-ink)] text-white hover:opacity-90 transition-opacity"
              >
                {lang === 'fr' ? 'Nous contacter' : 'Contact us'}
              </a>
            </div>

            {/* Erreur checkout */}
            {checkoutError && (
              <div className="mx-6 mb-2 text-center text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
                {checkoutError}
              </div>
            )}

            {/* Footer — Gérer l'abonnement si plan payant */}
            <div className="px-6 pb-5 flex flex-col items-center gap-2">
              {isPaid && (
                <>
                  <button
                    onClick={handlePortal}
                    className="text-xs text-[var(--color-accent-strong)] hover:text-[var(--color-accent)] transition-colors underline underline-offset-2 font-medium"
                  >
                    {lang === 'fr' ? '⚙️ Gérer mon abonnement (portail Stripe)' : '⚙️ Manage my subscription (Stripe portal)'}
                  </button>
                  {portalError && (
                    <p className="text-xs text-red-600">{portalError}</p>
                  )}
                </>
              )}
              <p className="text-xs text-[var(--color-faint)]">
                {lang === 'fr'
                  ? 'Questions ? Contactez-nous sur contact@wezea.net'
                  : 'Questions? Contact us at contact@wezea.net'}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
